import {LoaderFunctionArgs} from 'react-router-dom';
import {getRootStore} from '../../app.bootstrap';
import {getAppTracer} from '../../../app.tracer';
import {ILoginCallbackStore, LoginCallbackStore} from './login-callback.stores';
import {RouteName} from '../../router/route-names';
import {sendSentryLog} from '../../../error-reporting';

const trace = getAppTracer('login-callback.loader');

export async function loginCallbackRouteLoader(args: LoaderFunctionArgs): Promise<ILoginCallbackStore | void> {
    trace.info('Starting');
    const rootStore = getRootStore();
    const browserUtils = rootStore.browserUtilsService;

    const p = browserUtils.getParamValue('p');
    const e = browserUtils.getParamValue('e');
    const scanId = browserUtils.getParamValue('scanId');
    const authId = browserUtils.getParamValue('authId');
    const token = browserUtils.getParamValue('token');
    const r = browserUtils.getParamValue('r');
    const c = browserUtils.getParamValue('c');
    const initialState = browserUtils.getParamValue('is');
    const roomId = browserUtils.getParamValue('roomId');
    const g = browserUtils.getParamValue('g');
    const roomCode = browserUtils.getParamValue('roomCode');
    const observe = browserUtils.getParamValue('observe');
    const emb = browserUtils.getParamValue('emb');
    const mini = browserUtils.getParamValue('mini');
    const sw = browserUtils.getParamValue('sw');
    const audio = browserUtils.getParamValue('audio');
    const orgId = browserUtils.getParamValue('orgId');
    const prg = browserUtils.getParamValue('prg');
    const startDate = browserUtils.getParamValue('startDate');
    const endDate = browserUtils.getParamValue('endDate');
    const sophieCtx = browserUtils.getParamValue('sophie-ctx');
    const nav = rootStore.tsReactStateHelper;

    try {
        rootStore.authService.setAuthToken({token: token as string, authId: authId as string});
        rootStore.browserUtilsService.saveToLocalStorage('loginCallbackResult', !!authId);

        const roomIdentifier = roomId || roomCode || g;
        trace.info(`loginCallback has loaded room: ${roomIdentifier}, (Auth=${authId})`);

        if (initialState) {
            const lowerInitialState = initialState.toLowerCase();

            if (lowerInitialState.includes('reactivateaccount')) {
                trace.info('Redirecting to reactivate-account');
                await nav.navigateTo(RouteName.REACTIVATE_ACCOUNT, {
                    authId,
                    r,
                    p,
                    c,
                    roomId,
                    g,
                    roomCode,
                    audio,
                    emb,
                    mini,
                    sw,
                    prg,
                    orgId,
                    'sophie-ctx': sophieCtx,
                    observe: observe === 'guest' ? 'true' : observe
                });
            }

            if (lowerInitialState.includes('revokeaccount')) {
                trace.info('Redirecting to revoke-account');
                await nav.navigateTo(RouteName.REVOKE_ACCOUNT, {authId, orgId});
            }

            if (lowerInitialState.includes('vjhistory')) {
                trace.info('Redirecting to vj-history');
                await nav.navigateTo(RouteName.VJ_HISTORY, {authId, r, startDate, endDate, is: initialState, roomId});
            }

            if (lowerInitialState.includes('historyconsole')) {
                trace.info('Redirecting to historyConsole');
                await nav.navigateTo(RouteName.HISTORY_CONSOLE, {
                    authId,
                    r,
                    p,
                    e,
                    roomId,
                    startDate,
                    endDate,
                    scanId
                });
            }

            if (lowerInitialState.includes('history') && (r || p)) {
                trace.info('Redirecting to history');
                await nav.navigateTo(RouteName.HISTORY, {authId, customerId: r, customerNumber: p});
            }

            if (lowerInitialState.includes('historysearchbytags')) {
                trace.info('Redirecting to history (search by tags)');
                await nav.navigateTo(RouteName.HISTORY, {authId, isSearchByTags: true});
            }

            if (lowerInitialState.includes('admin')) {
                trace.info('Redirecting to admin');
                try {
                    await rootStore.redirectionService.goToAdmin();
                } catch (err) {
                    trace.error('Failed redirection to admin', {error: err});
                    sendSentryLog(err);
                    await nav.navigateTo(RouteName.LOGIN);
                }

                return new LoginCallbackStore();
            }
        }

        trace.info('Redirecting to invite');
        await nav.navigateTo(RouteName.INVITE, {
            authId,
            r,
            p,
            c,
            roomId,
            g,
            roomCode,
            audio,
            emb,
            mini,
            sw,
            prg,
            observe: observe === 'guest' ? 'true' : observe,
            startDate,
            endDate,
            'sophie-ctx': sophieCtx
        });
    } catch (err) {
        if (err instanceof Response) throw err;
        trace.error(`Failed to redirect from login callback (Auth=${authId})`, {error: err});
        sendSentryLog(err);
        await nav.navigateTo(RouteName.LOGIN);
    }
}
